import { PlusOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import type { RcFile } from 'antd/lib/upload/interface'
import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from '@vivaldis/antd-ui'

interface Props {
  defaultSrc: string | null | undefined
  fieldName: string
  value: RcFile | null | undefined
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldError: (field: string, message: string | undefined) => void
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void
}

export const ImageUpload: FC<Props> = ({
  defaultSrc,
  fieldName,
  value,
  setFieldValue,
  setFieldError,
  setFieldTouched
}) => {
  const [t] = useTranslation('web_ui')

  const [src, setSrc] = useState<string | null | undefined>(defaultSrc)

  useEffect(() => {
    let url: string | null = null

    const file = value

    if (file) {
      url = URL.createObjectURL(file)
      setSrc(url)

      return () => {
        if (url !== null) {
          URL.revokeObjectURL(url)
        }
      }
    } else if (defaultSrc) {
      setSrc(defaultSrc)
    }
  }, [defaultSrc, value])

  const handlePictureBeforeUpload = useCallback(
    (file: RcFile) => {
      // file should be less than 12MB
      const maxFileSizeInMB = 12
      const fileSizeInMB = file.size / 1024 / 1024

      const isValid = fileSizeInMB < maxFileSizeInMB

      if (!isValid) {
        setFieldError(
          fieldName,
          t(
            'edit_employee_personal_information_form.image_upload.errors.max_file_size',
            {
              count: maxFileSizeInMB
            }
          )
        )
        setFieldTouched(fieldName, true, false)
        return false
      }

      setFieldValue(fieldName, file, true)

      return file
    },
    [fieldName, setFieldError, setFieldTouched, setFieldValue, t]
  )

  const handlePictureRemove = useCallback(() => {
    setFieldValue(fieldName, null)
  }, [fieldName, setFieldValue])

  const uploadImageButton = (
    <div className={'uploadImageButton'}>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>
        {t(
          'edit_employee_personal_information_form.image_upload.upload_button'
        )}
      </div>
    </div>
  )

  return (
    <DocumentUpload
      listType="picture-card"
      beforeUpload={handlePictureBeforeUpload}
      onRemove={handlePictureRemove}
      fileList={[]}
      accept=".png, .jpg, .jpeg"
    >
      {src ? (
        <img src={src} style={{ width: '100%', maxHeight: '100%' }} />
      ) : (
        uploadImageButton
      )}
    </DocumentUpload>
  )
}

export const DocumentUpload = styled(Upload)`
  & .ant-upload {
    width: 100% !important;
    height: 160px !important;
  }
`
